import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CalendarPlus, CalendarX, Plane } from "lucide-react";

const EMP_TYPES = [
  { v: "sick", l: "Sick Leave" }, { v: "annual", l: "Annual Leave" }, { v: "personal", l: "Personal Leave" },
  { v: "unpaid", l: "Unpaid Leave" }, { v: "other", l: "Other Leave" },
];
const CON_TYPES = [
  { v: "unavailable", l: "Unavailable" }, { v: "planned_time_off", l: "Planned Time Off" }, { v: "other", l: "Other Unavailability" },
];
const TYPE_LABEL = { sick: "Sick Leave", annual: "Annual Leave", personal: "Personal Leave", unpaid: "Unpaid Leave",
  other: "Other", unavailable: "Unavailable", planned_time_off: "Planned Time Off" };
const STATUS_TONE = { submitted: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700", cancelled: "bg-slate-100 text-slate-500", draft: "bg-slate-100 text-slate-500" };

export default function LeaveOwnView() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ request_type: "", start_date: "", end_date: "", full_day: true, start_time: "", end_time: "", notes: "" });
  const load = () => api.get("/leave/me").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-slate-400">Loading…</div>;
  const isContractor = data.worker_type === "contractor";
  const types = isContractor ? CON_TYPES : EMP_TYPES;

  const submit = async () => {
    if (!form.request_type || !form.start_date) { toast.error("Select a type and start date"); return; }
    try {
      const r = await api.post("/leave", { ...form, end_date: form.end_date || form.start_date });
      const conf = r.data.job_conflicts?.length;
      toast.success(r.data.auto_approved ? "Availability recorded" : `Request submitted${conf ? ` (${conf} existing job(s) flagged)` : ""}`);
      setOpen(false); setForm({ request_type: "", start_date: "", end_date: "", full_day: true, start_time: "", end_time: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cancel = (id) => api.post(`/leave/${id}/cancel`).then(() => { toast.success("Cancelled"); load(); });

  return (
    <div className="space-y-5" data-testid="leave-own-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-navy">{isContractor ? "My Availability" : "My Leave & Availability"}</h1>
          <p className="text-slate-500 mt-1 text-sm">{isContractor ? "Let us know when you're unavailable for work." : "Request leave and view your balance."}</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="leave-request-btn" className="bg-navy hover:bg-navy/90">
          {isContractor ? <><CalendarX className="w-4 h-4 mr-2" />Add Unavailable Date</> : <><CalendarPlus className="w-4 h-4 mr-2" />Request Leave</>}
        </Button>
      </div>

      {!isContractor && data.balance && (
        <div className="grid grid-cols-3 gap-3" data-testid="leave-balance">
          <Bal label="Annual Entitlement" value={`${data.balance.annual_entitlement_days} days`} />
          <Bal label="Annual Used" value={`${data.balance.annual_used_days} days`} />
          <Bal label="Annual Remaining" value={`${data.balance.annual_remaining_days} days`} accent="text-emerald-600" />
        </div>
      )}

      <div className="space-y-2">
        {data.requests.length === 0 && <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-400">No requests yet.</div>}
        {data.requests.map((r) => (
          <div key={r.id} data-testid={`leave-item-${r.id}`} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500"><Plane className="w-4 h-4" /></div>
              <div>
                <p className="font-semibold text-navy text-sm">{TYPE_LABEL[r.request_type] || r.request_type}</p>
                <p className="text-xs text-slate-500">{formatDate(r.start_date)}{r.end_date !== r.start_date ? ` – ${formatDate(r.end_date)}` : ""}{!r.full_day ? ` · ${r.start_time}–${r.end_time}` : ""}</p>
                {r.status === "declined" && r.decline_reason && <p className="text-xs text-red-600 mt-0.5">Declined: {r.decline_reason}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_TONE[r.status]}`}>{r.status}</span>
              {["submitted", "approved"].includes(r.status) && (
                <button onClick={() => cancel(r.id)} data-testid={`leave-cancel-${r.id}`} className="text-xs text-slate-400 hover:text-red-600">Cancel</button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="leave-request-dialog">
          <DialogHeader>
            <DialogTitle>{isContractor ? "Add Unavailable Date" : "Request Leave"}</DialogTitle>
            <DialogDescription>{isContractor ? "Submit dates you won't be available." : "Submit a leave request for approval."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <select data-testid="leave-type-select" value={form.request_type} onChange={(e) => setForm({ ...form, request_type: e.target.value })} className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <option value="">Select…</option>
                {types.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" data-testid="leave-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" /></div>
              <div><Label>End Date</Label><Input type="date" data-testid="leave-end-date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.full_day} onChange={(e) => setForm({ ...form, full_day: e.target.checked })} data-testid="leave-fullday" />Full Day
            </label>
            {!form.full_day && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start Time</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1" /></div>
                <div><Label>End Time</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" /></div>
              </div>
            )}
            <div><Label>Reason / Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" rows={2} data-testid="leave-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="leave-submit-btn" className="bg-navy hover:bg-navy/90">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Bal({ label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4">
      <p className="text-xs text-slate-400 font-semibold">{label}</p>
      <p className={`text-lg font-heading font-bold mt-0.5 ${accent || "text-navy"}`}>{value}</p>
    </div>
  );
}
